// Debts list — grouped by class with per-variant summary cards, plus a create
// form (credit-card vs interest-free-facility field variants). Reuses the shared
// grouping + paid-down math; createDebt unchanged.
import { h, mount, type Child } from '../core/dom';
import { signal, type Signal } from '../core/signal';
import { bindInput } from '../core/bind';
import { groupDebtsByClass, paidDownPercent } from '@lib/debt';
import {
	formatError,
	DEBT_TYPES,
	type DebtView,
	type Category,
	type Account,
	type Frequency,
} from '@lib/db';
import { formatCurrency } from '@lib/format';
import { loadDebtsListData, createDebt } from '../data';
import { setHeaderControl } from '../components/headerSlot';
import { toast } from '../ui/toast';
import { loading } from '../ui/loading';

type Sig = Signal<string>;
const num = (s: string) => Number(s) || 0;

export function debtsListRoute(query?: URLSearchParams): Node {
	const container = h('div', {});
	setHeaderControl(null);
	// Dashboard debt tiles deep-link to their class section via ?class=...
	const targetClass = query?.get('class') ?? null;
	load();
	return container;

	function load(): void {
		container.replaceChildren(loading('Loading debts…'));
		loadDebtsListData()
			.then(({ debts, categories, accounts }) => build(debts, categories, accounts))
			.catch((e) => container.replaceChildren(h('div', { class: 'state-error' }, formatError(e as Error))));
	}

	function build(debts: DebtView[], categories: Category[], accounts: Account[]): void {
		const showCreate = signal(false);
		const formBox = h('div', {});
		mount(formBox, () => (showCreate() ? createForm(categories, accounts) : null));

		// "New debt" lives in the header bar (where month routes show the switcher).
		setHeaderControl(h('button', { class: 'btn btn-primary', onclick: () => showCreate.update((v) => !v) }, 'New debt'));

		const groups = groupDebtsByClass(debts).filter((g) => g.debts.length > 0);
		if (targetClass) {
			queueMicrotask(() =>
				container.querySelector(`[data-debt-class="${targetClass}"]`)?.scrollIntoView({ block: 'start' })
			);
		}
		container.replaceChildren(
			h(
				'div',
				{},
				formBox,
				...(groups.length === 0
					? [h('p', { class: 'muted' }, 'No debts yet.')]
					: groups.map((g) =>
							h('div', { class: 'section' }, h('h2', {}, g.label), h('div', { class: 'debt-grid' }, ...g.debts.map(debtCard)))
						))
			)
		);
	}

	function debtCard(d: DebtView): Child {
		const kids: Child[] = [
			h('div', { class: 'debt-card-title' }, d.name, h('span', { class: 'badge' }, d.debtType.replace(/_/g, ' '))),
		];
		if (d.debtType === DEBT_TYPES.MORTGAGE) {
			kids.push(row('Balance', formatCurrency(d.totalBalance ?? 0)), progress(d.equityPct ?? 0), sub(`${d.equityPct ?? 0}% equity · LVR ${d.lvrPct ?? 0}%`));
		} else if (d.debtType === DEBT_TYPES.INTEREST_FREE_FACILITY) {
			const pct = paidDownPercent(d.originalPrincipal ?? 0, d.currentBalance ?? 0);
			kids.push(
				row('Balance', formatCurrency(d.currentBalance ?? 0)),
				progress(pct),
				sub(`${pct}% paid down`),
				d.guardStatus === 'overdue'
					? h('span', { class: 'guard-chip overdue' }, 'payment overdue')
					: d.guardStatus === 'due'
						? h('span', { class: 'guard-chip due' }, 'payment due')
						: null
			);
		} else {
			const pct = paidDownPercent(d.legacyBalanceStart ?? 0, d.legacyRemaining);
			kids.push(row('Legacy remaining', formatCurrency(d.legacyRemaining)), progress(pct), sub(`${pct}% paid down`));
		}
		return h('a', { class: 'debt-card debt-link', href: `#/debts/${encodeURIComponent(d.id)}` }, ...kids);
	}

	// --- create form ---
	function createForm(categories: Category[], accounts: Account[]): Child {
		const debtType = signal<'credit_card' | 'interest_free_facility'>('credit_card');
		const paymentSource = signal<'bank' | 'payroll'>('bank');
		const f = {
			debtClass: signal('consumer'),
			name: signal(''),
			minimumPayment: signal(''),
			targetBalance: signal('0'),
			// facility
			originalPrincipal: signal(''),
			startDate: signal(''),
			endDate: signal(''),
			paymentFrequency: signal('monthly'),
			penaltyDeadline: signal(''),
			paymentCategory: signal(''),
			// card
			cardAccountId: signal(''),
			cardFormattedAccount: signal(''),
			legacyBalanceStart: signal(''),
			trackingStartDate: signal(''),
			legacyCategory: signal(''),
			interestCategory: signal(''),
		};

		const variantBox = h('div', {});
		mount(variantBox, () => (debtType() === 'interest_free_facility' ? facilityFields() : cardFields()));

		function submit(): void {
			if (!f.name().trim()) return;
			const payroll = paymentSource() === 'payroll';
			const common = { name: f.name().trim(), debtClass: f.debtClass(), paymentSource: paymentSource(), targetBalance: num(f.targetBalance()) };
			const payload =
				debtType() === 'interest_free_facility'
					? {
							...common,
							debtType: DEBT_TYPES.INTEREST_FREE_FACILITY,
							dataSource: 'hybrid',
							originalPrincipal: num(f.originalPrincipal()),
							startDate: f.startDate(),
							endDate: f.endDate(),
							minimumPayment: payroll ? 0 : num(f.minimumPayment()),
							paymentFrequency: f.paymentFrequency() as Frequency,
							penaltyDeadline: payroll ? '' : f.penaltyDeadline().trim(),
							paymentCategory: payroll ? '' : f.paymentCategory(),
						}
					: {
							...common,
							debtType: DEBT_TYPES.CREDIT_CARD,
							dataSource: 'feed',
							cardAccountId: f.cardAccountId().trim(),
							cardFormattedAccount: f.cardFormattedAccount().trim(),
							legacyBalanceStart: num(f.legacyBalanceStart()),
							trackingStartDate: f.trackingStartDate(),
							legacyCategory: f.legacyCategory(),
							interestCategory: f.interestCategory(),
							paymentCategory: f.paymentCategory(),
							minimumPayment: num(f.minimumPayment()),
						};
			createDebt(payload as Parameters<typeof createDebt>[0]).then(load).catch((e) => toast(formatError(e as Error)));
		}

		function facilityFields(): Child {
			const payroll = paymentSource() === 'payroll';
			return h(
				'div',
				{ class: 'form-grid' },
				labeled('Starting balance', dollar(f.originalPrincipal)),
				payroll ? null : labeled('Minimum payment', dollar(f.minimumPayment)),
				labeled('Payment frequency', selectSig(f.paymentFrequency, [['monthly', 'Monthly'], ['four-weekly', 'Four-weekly'], ['fortnightly', 'Fortnightly'], ['weekly', 'Weekly']])),
				labeled('Start date', dateSig(f.startDate)),
				labeled('End date', dateSig(f.endDate)),
				payroll ? null : labeled('Penalty day', inputSig(f.penaltyDeadline, 'e.g. 20')),
				payroll ? null : labeled('Payment category', categorySelect(f.paymentCategory, categories)),
				labeled('Target balance', dollar(f.targetBalance))
			);
		}

		function cardFields(): Child {
			return h(
				'div',
				{ class: 'form-grid' },
				labeled('Minimum payment', dollar(f.minimumPayment)),
				accounts.length > 0
					? labeled('Card account', accountSelect(f, accounts))
					: labeled('Card account ID', inputSig(f.cardAccountId, 'Akahu account id')),
				labeled('Legacy balance at start', dollar(f.legacyBalanceStart)),
				labeled('Tracking start date', dateSig(f.trackingStartDate)),
				labeled('Legacy category', categorySelect(f.legacyCategory, categories)),
				labeled('Interest category', categorySelect(f.interestCategory, categories)),
				labeled('Payment match category', categorySelect(f.paymentCategory, categories, 'Falls back to legacy paydown category')),
				labeled('Target balance', dollar(f.targetBalance))
			);
		}

		return h(
			'form',
			{ class: 'create-card', onsubmit: (e: SubmitEvent) => { e.preventDefault(); submit(); } },
			h(
				'div',
				{ class: 'form-grid' },
				labeled('Type', selectSig(debtType as Sig, [['credit_card', 'Credit card'], ['interest_free_facility', 'Interest-free facility']])),
				labeled('Class', selectSig(f.debtClass, [['consumer', 'Consumer'], ['asset_backed', 'Asset-backed'], ['monitored', 'Monitored']])),
				labeled('Paid from', selectSig(paymentSource as Sig, [['bank', 'Bank account'], ['payroll', 'PAYE (deducted)']])),
				labeled('Name', inputSig(f.name, 'Visa / Gem'))
			),
			variantBox,
			h('div', { class: 'row spacer-end' }, h('button', { class: 'btn btn-primary', type: 'submit' }, 'Create debt'))
		);

		function accountSelect(form: typeof f, accs: Account[]): HTMLSelectElement {
			const sel = h('select', { class: 'select' }, h('option', { value: '' }, 'Select…')) as HTMLSelectElement;
			for (const a of accs) sel.append(h('option', { value: a.id }, `${a.name}${a.formattedAccount ? ` · ${a.formattedAccount}` : ''}`));
			sel.addEventListener('change', () => {
				const acc = accs.find((a) => a.id === sel.value);
				form.cardAccountId.set(acc?.id ?? '');
				form.cardFormattedAccount.set(acc?.formattedAccount ?? '');
			});
			return sel;
		}
	}
}

// --- small shared builders ---
function row(label: string, value: string): Child {
	return h('div', { class: 'debt-row' }, h('span', {}, label), h('strong', {}, value));
}
function sub(t: string): Child {
	return h('div', { class: 'legacy-sub' }, t);
}
function progress(pct: number): Child {
	return h(
		'div',
		{ class: 'progress-bar', role: 'progressbar', 'aria-label': 'Paid down', 'aria-valuemin': '0', 'aria-valuemax': '100', 'aria-valuenow': String(Math.round(pct)) },
		h('div', { class: 'progress-fill', style: { width: `${pct}%` } })
	);
}
function labeled(label: string, control: HTMLElement): Child {
	return h('label', { class: 'field' }, h('span', { class: 'field-label' }, label), control);
}
function inputSig(sig: Signal<string>, placeholder = ''): HTMLInputElement {
	const i = h('input', { class: 'input', placeholder }) as HTMLInputElement;
	bindInput(i, sig);
	return i;
}
function dollar(sig: Signal<string>): HTMLElement {
	const i = h('input', { class: 'input', inputmode: 'decimal', placeholder: '0.00' }) as HTMLInputElement;
	bindInput(i, sig);
	return h('div', { class: 'dollar-input' }, h('span', { class: 'prefix' }, '$'), i);
}
function dateSig(sig: Signal<string>): HTMLInputElement {
	const i = h('input', { class: 'input', type: 'date' }) as HTMLInputElement;
	bindInput(i, sig, 'change');
	return i;
}
function selectSig(sig: Signal<string>, opts: [string, string][]): HTMLSelectElement {
	const sel = h('select', { class: 'select' }) as HTMLSelectElement;
	for (const [v, l] of opts) sel.append(h('option', { value: v }, l));
	bindInput(sel, sig, 'change');
	return sel;
}
function categorySelect(sig: Signal<string>, categories: Category[], emptyLabel = 'Select…'): HTMLSelectElement {
	const sel = h('select', { class: 'select' }, h('option', { value: '' }, emptyLabel)) as HTMLSelectElement;
	for (const c of categories) sel.append(h('option', { value: c.name }, c.name));
	bindInput(sel, sig, 'change');
	return sel;
}
